import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	OnChanges,
	SimpleChanges,
	ChangeDetectorRef,
	HostBinding
} from '@angular/core';
import { AlertController, Platform } from '@ionic/angular';
import { Subject, takeUntil } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { FABAction, FABConfig, FABState, FABActionType } from './models/fab-action.interface';
import {
	FABIntegrationService,
	FABContext,
	FormObject,
	UserRoles,
	DashboardContext
} from './services/fab-integration.service';
import { UserDataService } from 'src/app/providers/userData/user-data.service';

@Component({
	selector: 'app-enhanced-fab',
	standalone: false,
	templateUrl: './fab.component.html',
	styleUrls: ['./fab.component.scss'],
	animations: [
		trigger('menuAnimation', [
			state(
				'false',
				style({
					opacity: 0,
					visibility: 'hidden',
					transform: 'translateY(20px) scale(0.8)'
				})
			),
			state(
				'true',
				style({
					opacity: 1,
					visibility: 'visible',
					transform: 'translateY(0) scale(1)'
				})
			),
			transition('false => true', [animate('300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)')]),
			transition('true => false', [animate('200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)')])
		]),
		trigger('fadeInOut', [
			transition(':enter', [style({ opacity: 0 }), animate('200ms ease-out', style({ opacity: 1 }))]),
			transition(':leave', [animate('150ms ease-in', style({ opacity: 0 }))])
		])
	]
})
export class EnhancedFABComponent implements OnInit, OnDestroy, OnChanges {
	// Inputs compatible with existing footer component
	@Input() pageSpecificFormObject: FormObject | any = {};
	@Input() webCoCanSubmit: boolean = false;
	@Input() config: Partial<FABConfig> = {};
	@Input() disabled = false;
	@Input() hidden = false;

	// User context inputs
	// @Input() userRoles: UserRoles = {};
	// @Input() dashboardContext: DashboardContext = {};
	// @Input() loggedInUserName: string = '';

	// Output events (same as original footer component)
	@Output() footerEmitter: EventEmitter<string> = new EventEmitter<string>();
	@Output() actionExecuted = new EventEmitter<{ actionId: string; actionType: FABActionType }>();
	@Output() menuToggled = new EventEmitter<boolean>();
	@Output() primaryActionChanged = new EventEmitter<FABAction | null>();

	// Component state
	public state: FABState = {
		isExpanded: false,
		isLoading: false,
		keyboardVisible: false
	};

	// Responsive properties
	@HostBinding('class.mobile') isMobile = false;
	@HostBinding('class.tablet') isTablet = false;
	@HostBinding('class.desktop') isDesktop = false;

	// FAB configuration with defaults
	public fabConfig: FABConfig = {
		position: 'bottom-right',
		expandDirection: 'up',
		theme: 'claymorphism',
		safeAreaAware: true,
		primaryAction: undefined,
		secondaryActions: []
	};

	// Generated actions
	public actions: FABAction[] = [];
	public primaryAction?: FABAction;
	public secondaryActions: FABAction[] = [];
	public tertiaryActions: FABAction[] = [];
	public hasSecondaryActions = false;
	public showTooltip = false;

	private destroy$ = new Subject<void>();
	private longPressTimer?: number;
	private readonly longPressDelay = 500; // ms

	// Footer State
	// User Roles and Profile
	userRoles: UserRoles = {};
	loggedInUserName: any;
	profile: any = {};
	subscriptionStatus: boolean = true;

	// Dashboard Context
	dashboardContext: DashboardContext = {};

	constructor(
		public userdataService: UserDataService,
		public alertController: AlertController,
		private platform: Platform,
		private integrationService: FABIntegrationService,
		private cdr: ChangeDetectorRef
	) {
		this.updateResponsiveClasses();
	}

	async ngOnInit(): Promise<void> {
		this.setupConfiguration();
		this.setupResponsiveListeners();
		await this.getLoggedInUserProfile();
		await this.checkLoggedInUserRoles();
		await this.loadDashboardContext();
		console.log('FAB: User context loaded, generating actions');
		this.generateActions();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.clearLongPressTimer();
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Regenerate actions when form object or context changes
		if (
			changes['pageSpecificFormObject'] ||
			// changes['userRoles'] ||
			// changes['dashboardContext'] ||
			changes['webCoCanSubmit']
		) {
			console.log('FAB: ngOnChanges triggered, regenerating actions');
			this.generateActions();
		}
	}

	private loadDashboardContext = async () => {
		const section = await this.userdataService.getCurrentActiveSection();
		const dashboardContextDerived: DashboardContext = {
			isFROMIQACDASHBOARD: false,
			isFROM_CRITERIA_DASHBOARD: false,
			isFROMPERSONALDASHBOARD: false
		};
		if (section) {
			if (section === 'IQAC_DASHBORAD') {
				// this.isFROMIQACDASHBOARD = true;
				dashboardContextDerived.isFROMIQACDASHBOARD = true;
			} else if (section === 'PERSONAL_DASHBORD') {
				// this.isFROMPERSONALDASHBOARD = true;
				dashboardContextDerived.isFROMPERSONALDASHBOARD = true;
			} else if (section === 'CRITERIA_DASHBORAD') {
				// this.isFROM_CRITERIA_DASHBOARD = true;
				dashboardContextDerived.isFROM_CRITERIA_DASHBOARD = true;
			}
		}
		this.dashboardContext = dashboardContextDerived;
	};

	private getLoggedInUserProfile = async () => {
		const profile = await this.userdataService.getUserProfile();
		if (profile) {
			this.profile = profile;
			this.loggedInUserName = profile.emailId;
			if (this.profile.college.status == 'SUBS_EXPIRED') {
				this.subscriptionStatus = false;
			}
		}
	};

	private checkLoggedInUserRoles = async () => {
		const roles = await this.userdataService.getLoggedInUserRole();
		const derivedUserRoles: UserRoles = {};

		console.log(roles);
		if (roles) {
			if (roles.includes('IQAC_ADMIN')) {
				// this.isIQACADMIN = true;
				derivedUserRoles.isIQACADMIN = true;
			}
			if (roles.includes('WEB_EQUI')) {
				// this.isWEBEQUI = true;
				derivedUserRoles.isWEBEQUI = true;
			}
			if (roles.includes('OFFICE_EQUI')) {
				// this.isOFFICE_CO = true;
				derivedUserRoles.isOFFICE_CO = true;
			}
			if (roles.includes('TEACHER_EQUI')) {
				// this.isTEACHER = true;
				derivedUserRoles.isTEACHER = true;
			}
			if (roles.includes('LIB_EQUI')) {
				// this.isLibrarian = true;
				derivedUserRoles.isLibrarian = true;
			}
			if (roles.includes('CRITERIA_COORDINATOR')) {
				// this.isCriteriaCoordinator = true;
				derivedUserRoles.isCriteriaCoordinator = true;
			}
			if (
				(derivedUserRoles.isTEACHER && derivedUserRoles.isIQACADMIN) ||
				(derivedUserRoles.isTEACHER && derivedUserRoles.isCriteriaCoordinator) ||
				(derivedUserRoles.isTEACHER && derivedUserRoles.isOFFICE_CO) ||
				(derivedUserRoles.isTEACHER && derivedUserRoles.isWEBEQUI)
			) {
				derivedUserRoles.isDuelRole = true;
			}
			if (
				(derivedUserRoles.isIQACADMIN && derivedUserRoles.isOFFICE_CO) ||
				(derivedUserRoles.isIQACADMIN && derivedUserRoles.isWEBEQUI) ||
				(derivedUserRoles.isIQACADMIN && derivedUserRoles.isCriteriaCoordinator) ||
				(derivedUserRoles.isCriteriaCoordinator && derivedUserRoles.isWEBEQUI) ||
				(derivedUserRoles.isCriteriaCoordinator && derivedUserRoles.isOFFICE_CO) ||
				(derivedUserRoles.isOFFICE_CO && derivedUserRoles.isWEBEQUI)
			) {
				derivedUserRoles.isDuelRole = true;
			}
		}
		this.userRoles = derivedUserRoles;
	};

	/**
	 * Generate FAB actions based on current context
	 */
	private generateActions(): void {
		if (!this.pageSpecificFormObject) {
			console.log('FAB: No form object provided');
			return;
		}

		// Wait for essential user context to be loaded
		if (!this.loggedInUserName && !this.userRoles) {
			console.log('FAB: User context not loaded yet, waiting...');
			return;
		}

		const context: FABContext = {
			formObject: this.pageSpecificFormObject,
			userRoles: this.userRoles,
			dashboardContext: this.dashboardContext,
			loggedInUserName: this.loggedInUserName,
			webCoCanSubmit: this.webCoCanSubmit
		};

		// Use dual role logic if applicable
		this.actions = this.userRoles.isDuelRole
			? this.integrationService.generateDualRoleFABActions(context)
			: this.integrationService.generateFABActions(context);

		// Update action handlers to emit footer events
		this.actions.forEach((action) => {
			const originalHandler = action.handler;
			action.handler = async () => {
				await originalHandler();
				this.handleActionExecution(action);
			};
		});

		this.processActions();
	}

	titleValidationCheck() {
		let titleCheck = true;
		if (this.pageSpecificFormObject.title === undefined) {
			this.userdataService.presentToast('Please Enter Title', 'danger');
			titleCheck = false;
		} else if (this.pageSpecificFormObject.title.trim().length === 0) {
			this.userdataService.presentToast('Please Enter Title', 'danger');
			titleCheck = false;
		}
		return titleCheck;
	}

	populateForWithExtraAttributes() {
		if (this.pageSpecificFormObject.departmentIds) {
			// THIS IS HACK, Because SAVE API expects DepartmentIDs and update API expects department as attribute name... .so need to copy
			this.pageSpecificFormObject.department = [...this.pageSpecificFormObject.departmentIds];
		}
		if (this.pageSpecificFormObject.committeIds) {
			// THIS IS HACK, Because SAVE API expects committeIds and update API expects committee as attribute name... .so need to copy
			this.pageSpecificFormObject.committee = [...this.pageSpecificFormObject.committeIds];
		}
		if (this.pageSpecificFormObject.inchargeUserIds) {
			this.pageSpecificFormObject.inchargeUser = [...this.pageSpecificFormObject.inchargeUserIds];
		}
		if (this.pageSpecificFormObject.involvedUserIds) {
			this.pageSpecificFormObject.involvedUser = [...this.pageSpecificFormObject.involvedUserIds];
		}
	}

	async presentRecalledFormAlert() {
		const alert = await this.alertController.create({
			header: 'Recall form',
			// subHeader: staffName,
			inputs: [
				{
					name: 'recalledMessage',
					type: 'textarea',
					placeholder: 'Type your message here'
				}
			],
			buttons: [
				{
					text: 'Recall',
					role: 'Ok',
					handler: (data: any) => {
						if (data.recalledMessage != '') {
							// this.recallForm(data);
							this.footerEmitter.emit('recallForm');
							this.pageSpecificFormObject.rejectionMessage = data.recalledMessage;
						} else {
							this.userdataService.presentToast('Please add your message', 'warning');
						}
					}
				},
				{
					text: 'Cancel',
					role: 'Cancel',
					handler: () => {
						console.log('cancel click');
					}
				}
			]
		});
		alert.present();
	}

	async presentResentFormAlert() {
		const alert = await this.alertController.create({
			header: 'Return form',
			// subHeader: staffName,
			inputs: [
				{
					name: 'resentMessage',
					type: 'textarea',
					placeholder: 'Type your message here'
				}
			],
			buttons: [
				{
					text: 'Return',
					role: 'Ok',
					handler: (data) => {
						if (data.resentMessage != '' && data.resentMessage.trim() != 0) {
							// this.resentForm(data);
							this.footerEmitter.emit('resentForm');
							this.pageSpecificFormObject.rejectionMessage = data.resentMessage;
						} else {
							this.userdataService.presentToast('Please add your message', 'warning');
						}
					}
				},
				{
					text: 'Cancel',
					role: 'Cancel',
					handler: () => {
						console.log('cancel click');
					}
				}
			]
		});
		alert.present();
	}

	async presentSubmitForApprovalFormAlert() {
		const alert = await this.alertController.create({
			header: 'Submit form for approval',
			// subHeader: staffName,
			inputs: [
				{
					name: 'submitMessage',
					type: 'textarea',
					placeholder: 'Type your message here'
				}
			],
			buttons: [
				{
					text: 'Submit',
					role: 'Ok',
					handler: (data: any) => {
						if (data.submitMessage != '' && data.submitMessage.trim() != 0) {
							// this.sendFormForApproval(data);
							this.pageSpecificFormObject.rejectionMessage = data.submitMessage;
							this.footerEmitter.emit('sendFormForApproval');
						} else {
							this.userdataService.presentToast('Please add your message', 'warning');
						}
					}
				},
				{
					text: 'Cancel',
					role: 'Cancel',
					handler: () => {
						console.log('cancel click');
					}
				}
			]
		});
		alert.present();
	}

	/**
	 * Handle action execution - emit appropriate footer events
	 */
	private handleActionExecution(action: FABAction): void {
		const actionType = action.id.split('_')[0] as FABActionType;

		// Emit the original footer events for backward compatibility
		switch (actionType) {
			case 'SUBMIT':
				if (this.titleValidationCheck()) {
					this.populateForWithExtraAttributes();
					this.presentSubmitForApprovalFormAlert();
					// this.footerEmitter.emit("sendFormForApproval");
				}
				break;
			case 'UPDATE':
				console.log(`Update form`);
				this.populateForWithExtraAttributes();
				this.footerEmitter.emit('updateForm');
				break;
			case 'APPROVE':
				this.populateForWithExtraAttributes();
				this.footerEmitter.emit('approvedForm');
				break;
			case 'CREATE':
				console.log(`Creating new form`);
				if (this.titleValidationCheck()) {
					this.footerEmitter.emit('submitForm');
				}
				break;
			case 'FORWARD':
				this.populateForWithExtraAttributes();
				this.footerEmitter.emit('forwordForm');
				break;
			case 'SHARE':
				this.populateForWithExtraAttributes();
				this.footerEmitter.emit('formSharing');
				break;
			case 'RECALL':
				this.populateForWithExtraAttributes();
				this.presentRecalledFormAlert();
				// this.footerEmitter.emit('recallForm');
				break;
			case 'RESEND':
				if (this.titleValidationCheck()) {
					this.populateForWithExtraAttributes();
					this.presentResentFormAlert();
					// this.footerEmitter.emit("resentForm");
				}
				break;
			case 'REVERT':
				this.populateForWithExtraAttributes();
				this.footerEmitter.emit('rejectForm');
				break;
			case 'REPLICATE':
				this.populateForWithExtraAttributes();
				this.footerEmitter.emit('replicateForm');
				break;
			case 'DELETE':
				this.populateForWithExtraAttributes();
				this.footerEmitter.emit('deleteForm');
				break;
			case 'SUBMIT_WEBC':
				this.populateForWithExtraAttributes();
				this.footerEmitter.emit('submitForApprovalWebC');
				break;
		}

		// Also emit the new action executed event
		this.actionExecuted.emit({
			actionId: action.id,
			actionType: actionType
		});
	}

	/**
	 * Process actions and determine hierarchy
	 */
	private processActions(): void {
		// Filter visible actions
		const visibleActions = this.actions.filter((action) => action.visible && !action.disabled);

		// Categorize actions by priority
		const originalSecondaryActions = visibleActions.filter((action) => action.priority === 'secondary');
		const originalTertiaryActions = visibleActions.filter((action) => action.priority === 'tertiary');

		// Determine primary action using smart logic
		this.primaryAction = this.determinePrimaryAction(visibleActions);

		// Include primary action in menu when there are other actions, but avoid duplicates
		const menuActions = [];
		const hasOtherActions = originalSecondaryActions.length > 0 || originalTertiaryActions.length > 0;

		if (this.primaryAction && hasOtherActions) {
			// Check if primary action already exists in secondary/tertiary actions (avoid duplicates)
			const primaryActionType = this.primaryAction.id.split('_')[0]; // Extract action type (e.g., 'UPDATE', 'SUBMIT')
			const isDuplicateInMenu = [...originalSecondaryActions, ...originalTertiaryActions].some(
				(action) => action.id.split('_')[0] === primaryActionType
			);

			if (!isDuplicateInMenu) {
				menuActions.push(this.primaryAction);
			}
		}
		menuActions.push(...originalSecondaryActions, ...originalTertiaryActions);

		// Set the actions that will be rendered in the menu
		// Template reads from secondaryActions and tertiaryActions arrays
		this.secondaryActions = menuActions;
		this.tertiaryActions = []; // Clear tertiary since we're putting everything in secondary

		this.hasSecondaryActions = this.secondaryActions.length > 0;

		// Update FAB config
		this.fabConfig.primaryAction = this.primaryAction;
		this.fabConfig.secondaryActions = menuActions;

		console.log(
			'FAB: Menu actions being set:',
			menuActions.map((a) => ({ id: a.id, label: a.label, priority: a.priority }))
		);
		console.log(
			'FAB: secondaryActions >>',
			this.secondaryActions.map((a) => ({ id: a.id, label: a.label, priority: a.priority }))
		);
		console.log('FAB: primaryAction >>', this.primaryAction.id, this.primaryAction.label, this.primaryAction.priority);

		// Emit primary action change
		this.primaryActionChanged.emit(this.primaryAction);

		this.cdr.detectChanges();
	}

	/**
	 * Determine primary action based on form status and available actions
	 */
	private determinePrimaryAction(actions: FABAction[]): FABAction | undefined {
		// Priority order based on form workflow
		console.log(
			'Visible Actions >>',
			actions.map((a) => ({ id: a.id, label: a.label, priority: a.priority }))
		);
		const priorityOrder: FABActionType[] = [
			'CREATE',
			'SUBMIT',
			'UPDATE',
			'APPROVE',
			'FORWARD',
			'SHARE',
			'RECALL',
			'RESEND',
			'REVERT',
			'REPLICATE',
			'DELETE',
			'SUBMIT_WEBC'
		];

		// Find highest priority available action
		for (const actionType of priorityOrder) {
			const action = actions.find((a) => a.id.includes(actionType) && !a.disabled);
			if (action) {
				return action;
			}
		}

		// Fallback: return first available action
		return actions.find((action) => !action.disabled);
	}

	// ===============================================================
	// Original FAB Component Methods (delegated)
	// ===============================================================

	private setupConfiguration(): void {
		this.fabConfig = {
			...this.fabConfig,
			...this.config
		};

		// Apply responsive overrides if they exist
		const configWithResponsive = this.config as any;
		if (this.isMobile && configWithResponsive.mobile) {
			this.fabConfig = { ...this.fabConfig, ...configWithResponsive.mobile };
		} else if (this.isTablet && configWithResponsive.tablet) {
			this.fabConfig = { ...this.fabConfig, ...configWithResponsive.tablet };
		} else if (this.isDesktop && configWithResponsive.desktop) {
			this.fabConfig = { ...this.fabConfig, ...configWithResponsive.desktop };
		}
	}

	private setupResponsiveListeners(): void {
		// Responsive handling code...
		this.updateResponsiveClasses();
	}

	private updateResponsiveClasses(): void {
		const width = window.innerWidth;
		const height = window.innerHeight;

		this.isMobile = width < 768;
		this.isTablet = width >= 768 && width < 1024;
		this.isDesktop = width >= 1024;
	}

	// Event Handlers
	public onPrimaryActionClick(): void {
		if (this.disabled) {
			return;
		}

		// If there are secondary actions, toggle the menu instead of executing primary action
		if (this.hasSecondaryActions) {
			this.toggleMenu();
			return;
		}

		// If no secondary actions, execute the primary action
		if (!this.primaryAction || this.primaryAction.disabled) {
			this.userdataService.presentToast('No Action available!', 'warning');
			return;
		}

		if (this.primaryAction.confirmationRequired) {
			this.showConfirmation(this.primaryAction);
		} else {
			this.executePrimaryAction();
		}
	}

	public onPrimaryActionLongPress(): void {
		if (this.disabled || !this.hasSecondaryActions) {
			return;
		}
		this.toggleMenu();
	}

	public onSecondaryActionClick(action: FABAction): void {
		if (action.disabled) return;

		this.closeMenu();

		if (action.confirmationRequired) {
			this.showConfirmation(action);
		} else {
			this.executeAction(action);
		}
	}

	public toggleMenu(): void {
		if (!this.hasSecondaryActions || this.disabled) return;

		this.state.isExpanded = !this.state.isExpanded;
		this.menuToggled.emit(this.state.isExpanded);

		if (this.state.isExpanded) {
			this.addBodyClass('fab-menu-open');
		} else {
			this.removeBodyClass('fab-menu-open');
		}
	}

	public closeMenu(): void {
		if (this.state.isExpanded) {
			this.state.isExpanded = false;
			this.menuToggled.emit(false);
			this.removeBodyClass('fab-menu-open');
		}
	}

	private async executePrimaryAction(): Promise<void> {
		if (!this.primaryAction) return;

		this.state.isLoading = true;
		this.cdr.detectChanges();

		try {
			await this.executeAction(this.primaryAction);
		} finally {
			this.state.isLoading = false;
			this.cdr.detectChanges();
		}
	}

	private async executeAction(action: FABAction): Promise<void> {
		try {
			action.loading = true;
			this.cdr.detectChanges();

			await action.handler();

			// Show success feedback
			this.showSuccessState();
		} catch (error) {
			console.error('FAB action execution failed:', error);
			this.showErrorState();
		} finally {
			action.loading = false;
			this.cdr.detectChanges();
		}
	}

	private showConfirmation(action: FABAction): void {
		// Use native confirm for now - will be replaced with Ionic AlertController
		const message = action.confirmationMessage || `Do you want to ${action.label} form?`;
		if (window.confirm(message)) {
			this.executeAction(action);
		}
	}

	private showSuccessState(): void {
		// Visual feedback implementation
		console.log('Action completed successfully');
	}

	private showErrorState(): void {
		// Error state implementation
		console.log('Action failed');
	}

	private clearLongPressTimer(): void {
		if (this.longPressTimer) {
			clearTimeout(this.longPressTimer);
			this.longPressTimer = undefined;
		}
	}

	private addBodyClass(className: string): void {
		document.body.classList.add(className);
	}

	private removeBodyClass(className: string): void {
		document.body.classList.remove(className);
	}

	// Getters for template
	get fabPositionClasses(): string[] {
		const classes = [`position-${this.fabConfig.position}`];

		if (this.state.keyboardVisible) {
			classes.push('keyboard-visible');
		}

		if (this.state.isExpanded) {
			classes.push('expanded');
		}

		if (this.state.isLoading) {
			classes.push('loading');
		}

		if (this.hidden) {
			classes.push('hidden');
		}

		return classes;
	}

	get menuDirectionClass(): string {
		return `expand-${this.fabConfig.expandDirection}`;
	}

	/**
	 * Public method to manually refresh actions (useful for dynamic updates)
	 */
	public refreshActions(): void {
		console.log('FAB: Manual refresh triggered');
		this.generateActions();
	}

	/**
	 * Public method to check if a specific action is available
	 */
	public hasAction(actionType: FABActionType): boolean {
		return this.actions.some((action) => action.id.includes(actionType));
	}

	/**
	 * Public method to enable/disable specific actions
	 */
	public setActionDisabled(actionType: FABActionType, disabled: boolean): void {
		const action = this.actions.find((a) => a.id.includes(actionType));
		if (action) {
			action.disabled = disabled;
			this.processActions();
		}
	}
}
